import { AlertDialog, Button, Heading1, Heading2 } from '@hap/components'
import { useQuery } from '@tanstack/react-query'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useState } from 'react'

import { LinkButton } from '~/features/navigation'
import { useTitle } from '~/hooks'
import { HighlightCategoryV3Mutators } from '~/mutators/highlight-categories-v3'
import { HighlightV3Mutators } from '~/mutators/highlights-v3'
import { RouterOutput, useTRPC } from '~/services/trpc'

import { CategoryDialog } from './highlights/-components/category-dialog'
import {
  createColumns,
  HighlightRow,
  Table,
} from './highlights/-components/table-config'

export const Route = createFileRoute('/_authenticated/$alias/_app/highlights')({
  component: HighlightsPage,
  loader: async ({ context: { trpc, queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(
        trpc.highlightCategoriesV3.list.queryOptions(),
      ),
      queryClient.ensureQueryData(trpc.highlightsV3.list.queryOptions()),
    ])
    return
  },
})

type HighlightCategory = RouterOutput['highlightCategoriesV3']['list'][number]

function HighlightsPage() {
  useTitle('Highlights')
  const trpc = useTRPC()

  const [createCategoryOpen, setCreateCategoryOpen] = useState(false)

  const { data: categories = [] } = useQuery(
    trpc.highlightCategoriesV3.list.queryOptions(),
  )
  const { data: highlights = [] } = useQuery(
    trpc.highlightsV3.list.queryOptions(),
  )

  return (
    <div className="relative flex h-full items-stretch">
      <div className="flex-1 overflow-y-auto px-2 py-4">
        <header className="mb-8 flex items-center justify-between gap-4 border-b px-2 pb-8">
          <hgroup>
            <Heading1>Highlights</Heading1>
            <p className="text-accent-foreground">
              Define the moments, behaviors, and signals you want Happitu to
              identify across conversations.
            </p>
          </hgroup>
          <Button onClick={() => setCreateCategoryOpen(true)} icon="plus">
            Category
          </Button>
        </header>

        <div className="space-y-8 px-2">
          {categories.map((category) => (
            <HighlightCategoryCard category={category} key={category.id} />
          ))}

          {categories.length === 0 && highlights.length === 0 && (
            <div className="text-muted-foreground py-12 text-center">
              No highlights yet. Create your first category and highlight to get
              started.
            </div>
          )}
        </div>

        <CategoryDialog
          open={createCategoryOpen}
          onOpenChange={setCreateCategoryOpen}
        />
      </div>

      <Outlet />
    </div>
  )
}

function HighlightCategoryCard({ category }: { category: HighlightCategory }) {
  const [deleteCategoryOpen, setDeleteCategoryOpen] = useState(false)
  const [deleteHighlightId, setDeleteHighlightId] = useState<string | null>(
    null,
  )

  const { delete: deleteCategory } = HighlightCategoryV3Mutators.useDelete({
    onSuccess: () => setDeleteCategoryOpen(false),
  })
  const { delete: deleteHighlight } = HighlightV3Mutators.useDelete({
    onSuccess: () => setDeleteHighlightId(null),
  })

  const table = useReactTable({
    columns: createColumns({
      onDeleteHighlight: (highlightId) => setDeleteHighlightId(highlightId),
    }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    data: (category.highlights ?? []) as HighlightRow[],
  })

  return (
    <div className="flex-1">
      <div className="bg-accent flex-1 rounded-lg p-2">
        <header className="flex shrink-0 grow-0 items-center justify-between gap-3 p-2">
          <Heading2 className="pl-2 text-lg font-medium">
            {category.name}
          </Heading2>
          <div className="flex items-center gap-1">
            <LinkButton
              icon="plus"
              variant="outline"
              to="/$alias/highlights/$highlightCategoryId/new"
              from={Route.fullPath}
              params={{ highlightCategoryId: category.id }}
            >
              Highlight
            </LinkButton>
            <Button
              icon="trash"
              size="icon"
              variant="outline"
              aria-label={`Delete ${category.name}`}
              onClick={() => setDeleteCategoryOpen(true)}
            />
          </div>
        </header>
        <Table table={table} />
      </div>

      <AlertDialog
        open={deleteCategoryOpen}
        onOpenChange={setDeleteCategoryOpen}
        title="Delete category"
        description={
          category.highlights?.length
            ? `This category currently has ${category.highlights.length} highlight${
                category.highlights.length === 1 ? '' : 's'
              }. Delete it only if you're ready to remove the category.`
            : 'This category will be permanently deleted. This action cannot be undone.'
        }
        action={{
          variant: 'destructive',
          value: 'Delete',
          onClick: () => deleteCategory(category.id),
        }}
      />

      <AlertDialog
        open={!!deleteHighlightId}
        onOpenChange={(open) => {
          if (!open) setDeleteHighlightId(null)
        }}
        title="Delete highlight"
        description="This highlight will be permanently deleted. This action cannot be undone."
        action={{
          variant: 'destructive',
          value: 'Delete',
          onClick: () => {
            if (deleteHighlightId) deleteHighlight(deleteHighlightId)
          },
        }}
      />
    </div>
  )
}
